/**
 * WasmEngineBinding — web impl that drives the genz-engine WASM module hosted
 * in a Web Worker (off the main thread).
 *
 * The real Rust→WASM engine (wasm-pack, built by scripts/build-wasm.sh) runs
 * INSIDE the worker; this main-thread binding round-trips every call through the
 * worker protocol (renderBatch streams progress/trace back). A worker is REQUIRED
 * — there is no mock fallback: if the engine can't load, calls reject with a clear
 * error so the failure is visible (never silently faked).
 */
import type { EngineBinding, ComposeMockupArgs, MagentaMarkerResult } from "./binding.js";
import { ENGINE_VERSION } from "./version.js";
import type {
  Detection,
  ImageInput,
  InputValidation,
  JobResult,
  Mask,
  Polygon,
  ProgressCallback,
  RenderBatchJob,
  RenderOutput,
  RenderRequest,
  TemplateAssetSet,
  TraceCallback,
  ValidationResult,
} from "./types.js";
import type { WorkerLike, WorkerRequest, WorkerResponse } from "./worker-protocol.js";
import { WEB_ENGINE_CAPABILITIES } from "./web-capabilities.js";

/** Distributes Omit over each union member so the discriminant stays correlated. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export interface WasmEngineOptions {
  /** The render Web Worker hosting the WASM module. REQUIRED. */
  worker: WorkerLike;
  engineVersion?: string;
}

/**
 * Why the WEB build does not implement an optional `EngineBinding` member (F-49 / AUD-3 IM-7).
 *
 * These are DECISIONS, not gaps. For most of them the absence is load-bearing: the call site
 * branches on it and picks the correct web path (IndexedDB instead of the encrypted native
 * store, `perceptualHash` instead of the ONNX embedding, the classical remover instead of the
 * AI matte, the per-item compose instead of a render session). A throwing stub would turn a
 * working fallback into a runtime error; a plausible-looking stub would turn it into a lie.
 *
 * Each string is written to be READ — by the next engineer, and (where a surface shows it) by
 * an operator. `web-parity.test.ts` holds this table and the class to being exact complements.
 */
export const WEB_UNSUPPORTED: Readonly<Record<string, string>> = {
  removeBackgroundAI:
    "The on-device AI matte (BiRefNet / U²-Net-p) needs the ONNX runtime, which is compiled into the desktop build only. The editor disables “Remove background (AI)” and says so in its tooltip; the magenta key and the classical remover both work on web.",
  loadSegmentationModel:
    "Caching the ~109 MB segmenter native-side is meaningless without `removeBackgroundAI`; the web build never holds a model.",
  segmenterInfo:
    "There is no segmenter to report on. The editor’s AI-matte line is deliberately hidden rather than rendered with empty values, because a line reading “CPU · model not loaded” would describe a feature this build does not have.",
  imageEmbedding:
    "The neural view embedding is native (`ml`) only; callers fall back to `perceptualHash`, which the web build does implement (spec 016).",
  backendId:
    "D-12 scopes the layered `contentHash` promise to “same engine + same backend”. The web build has exactly one backend and the interface already documents that an absent `backendId` is read as `\"cpu\"` — which is the truth here — so setting it would add a second place to keep that truth in sync with no second answer to hold.",
  storeImage:
    "The encrypted-at-rest local image store is a desktop file-system feature; on web `stores/imageStore.ts` uses IndexedDB, and it selects that path by this method being absent.",
  loadImage:
    "See `storeImage` — web reads the same images back from IndexedDB, selected by this method being absent.",
  deleteImage:
    "See `storeImage` — web deletes from IndexedDB, selected by this method being absent.",
  openMockupSession:
    "WS-H2’s pinned decision: the render session keeps decoded assets in a native handle table across IPC calls, which the web engine (a Worker with no cross-call handle table, rendering single-user previews rather than 500-design exports) has no equivalent for. Web keeps exercising the no-session fallback on every run.",
  composeInSession:
    "See `openMockupSession` — the web engine has no native handle table, so it composes per item on every run.",
  closeMockupSession:
    "See `openMockupSession` — the web engine never opens one, so there is no native session to close.",
  renderPrintFile:
    "The DPI-scaled print file is rendered by the desktop engine (D-10); on web the export ships the design’s original uploaded artwork, which is what the call site’s `?.` already selects.",
};

export class WasmEngineBinding implements EngineBinding {
  readonly impl = "wasm" as const;
  engineVersion: string;
  private worker: WorkerLike;
  private nextId = 1;
  private pending = new Map<
    number,
    { resolve: (v: unknown) => void; reject: (e: Error) => void; onProgress?: ProgressCallback; onTrace?: TraceCallback }
  >();
  private listener?: (ev: { data: WorkerResponse }) => void;
  /** Why the worker can no longer answer; set once, never cleared. */
  private deadReason?: string;

  constructor(opts: WasmEngineOptions) {
    if (!opts?.worker) {
      throw new Error("WasmEngineBinding requires a render worker (no mock fallback).");
    }
    this.worker = opts.worker;
    this.engineVersion = opts.engineVersion ?? ENGINE_VERSION;
    this.attach(this.worker);
    // Best-effort: replace the fallback identity with the engine's real version.
    void this.call<string>({ op: "version" })
      .then((v) => { if (typeof v === "string" && v) this.engineVersion = v; })
      .catch(() => { /* surfaced by the first real op if the engine is down */ });
  }

  private attach(worker: WorkerLike): void {
    this.listener = (ev) => this.onMessage(ev.data);
    worker.addEventListener("message", this.listener);
    // A worker that CRASHES (wasm panic, OOM, failed module load) delivers no message, so
    // every pending render used to hang forever — spinner stuck, buffers pinned in the map,
    // and every later call joining the same void with no log. Reject the outstanding work
    // loudly instead. `error`/`messageerror` are optional on the WorkerLike surface, so this
    // is feature-detected (Node/test doubles simply skip it).
    const onFatal = (what: string) => () => this.markDead(
      `Render worker ${what} — the rendering engine stopped. Reload the page (or restart the app) to recover.`,
    );
    const add = (worker as unknown as {
      addEventListener?: (t: string, cb: () => void) => void;
    }).addEventListener?.bind(worker);
    if (add) {
      add("error", onFatal("crashed"));
      add("messageerror", onFatal("could not deserialize a message"));
    }
  }

  /** Fail every in-flight call, and every later one, with one message (worker death / dispose). */
  private markDead(message: string): void {
    this.deadReason ??= message;
    const entries = [...this.pending.values()];
    this.pending.clear();
    for (const e of entries) e.reject(new Error(message));
  }

  private onMessage(msg: WorkerResponse): void {
    const entry = this.pending.get(msg.id);
    if (!entry) return;
    if ("stream" in msg) {
      if (msg.stream === "progress") entry.onProgress?.(msg.progress);
      else if (msg.stream === "trace") entry.onTrace?.(msg.trace);
      return;
    }
    this.pending.delete(msg.id);
    if (msg.ok) entry.resolve((msg as { result: unknown }).result);
    else entry.reject(new Error(msg.error));
  }

  private call<T>(
    req: DistributiveOmit<WorkerRequest, "id">,
    cbs?: { onProgress?: ProgressCallback; onTrace?: TraceCallback },
  ): Promise<T> {
    if (this.deadReason) return Promise.reject(new Error(this.deadReason));
    const worker = this.worker;
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        onProgress: cbs?.onProgress,
        onTrace: cbs?.onTrace,
      });
      worker.postMessage({ id, ...req } as WorkerRequest);
    });
  }

  async validateInputs(imgs: ImageInput[]): Promise<InputValidation> {
    return this.call({ op: "validateInputs", imgs });
  }
  async perceptualHash(image: Uint8Array): Promise<string> {
    return this.call({ op: "perceptualHash", image });
  }
  async composeMockup(args: ComposeMockupArgs): Promise<Uint8Array | null> {
    return this.call({ op: "composeMockup", args });
  }
  async detectMagentaMarker(
    image: Uint8Array,
    threshold = 0,
    targetColor: [number, number, number] | null = null,
    tolerance = 0,
  ): Promise<MagentaMarkerResult> {
    return this.call({ op: "detectMagentaMarker", image, threshold, target: targetColor, tolerance });
  }
  async detectShadedMagentaMarker(image: Uint8Array, threshold = 0): Promise<MagentaMarkerResult> {
    return this.call({ op: "detectShadedMagentaMarker", image, threshold });
  }
  async refine(caseImg: ImageInput, proposal: unknown): Promise<Detection> {
    return this.call({ op: "refine", caseImg, proposal });
  }
  async assemble(detection: Detection, caseImg: ImageInput): Promise<TemplateAssetSet> {
    return this.call({ op: "assemble", detection, caseImg });
  }
  async geometryValidate(printArea: Polygon, cutouts: Mask[]): Promise<ValidationResult> {
    return this.call({ op: "geometryValidate", printArea, cutouts });
  }
  async effectivePrintArea(printArea: Polygon, cutouts: Mask[]): Promise<Polygon> {
    return this.call({ op: "effectivePrintArea", printArea, cutouts });
  }
  async simplify(polygon: Polygon, epsilon: number): Promise<Polygon> {
    return this.call({ op: "simplify", polygon, epsilon });
  }
  async renderOne(req: RenderRequest): Promise<RenderOutput> {
    return this.call({ op: "renderOne", req });
  }
  async renderBatch(job: RenderBatchJob, onProgress?: ProgressCallback, onTrace?: TraceCallback): Promise<JobResult> {
    return this.call({ op: "renderBatch", job }, { onProgress, onTrace });
  }

  // --- WS-N / F-49: the six answerable web diagnostics --------------------------------------
  // Build-time facts (`scripts/build-wasm.sh`: `--no-default-features --features wasm`), so they
  // do NOT round-trip through the worker protocol — there is no `op` whose answer could differ.
  // Assigned one by one rather than spread so `tsc` checks each against `EngineBinding`.
  compositorInfo = WEB_ENGINE_CAPABILITIES.compositorInfo;
  backendInfo = WEB_ENGINE_CAPABILITIES.backendInfo;
  hasMl = WEB_ENGINE_CAPABILITIES.hasMl;
  releaseSegmentationModel = WEB_ENGINE_CAPABILITIES.releaseSegmentationModel;
  cancelBackgroundRemoval = WEB_ENGINE_CAPABILITIES.cancelBackgroundRemoval;
  setRenderBackend = WEB_ENGINE_CAPABILITIES.setRenderBackend;

  dispose(): void {
    if (this.listener) this.worker.removeEventListener("message", this.listener);
    // Terminating with calls in flight abandoned their promises forever — settle them first.
    this.markDead("Render worker was disposed — this render cannot finish.");
    this.worker.terminate?.();
  }
}

export function createWasmEngine(opts: WasmEngineOptions): EngineBinding {
  return new WasmEngineBinding(opts);
}
